/*
 * @Description: 使用 Promise 封装请求操作
 * @Author: erickuang
 * @Date: 2021-08-16 20:50:30
 * @LastEditor: erickuang
 * @LastEditTime: 2022-07-04 15:16:28
 */
import Aegis from '@tencent/aegis-web-sdk';
import getAegis from './aegis';
import store from '@/store';
import { FrontOperationType, VJSBridgeFront } from '@/libs/VJSBridgeFront';
import { ILinkType, VJSBridge, OperationPayloadRequestInterior } from '@/libs/VJSBridge';
import { ResponseData } from '@/types/request';
import MockData from './data-dev';

const aegis = getAegis();

const TIME_OUT_MS = 5000;
const TIME_OUT_ERR_CODE = 500;
const SUCCESS_ERR_CODE = 0;

export const requestILink = async <T>(
  params: OperationPayloadRequestInterior = { cmd: '' },
  {
    iLinkType = ILinkType.Main,
    withCallback = true,
    withTicket = true,
  }: Partial<{
    iLinkType: ILinkType;
    withCallback: boolean;
    withTicket: boolean;
  }> = {},
): Promise<T> => {
  const { cmd } = params;
  if (!cmd) {
    console.error(`In request method. Cmd "${cmd}" invalid. Params: `, params);
    return false as unknown as T;
  }
  if (!withCallback) return Promise.resolve(true as unknown as T);
  const requestPromise = devAutoExecuteWrapper(
    cmd,
    timeoutPromiseWrapper<T>((res, rej) => {
      const validParams = { ...params };
      withTicket && (validParams.ticket = (store as any)?.getters['account/ticket'] || '');
      VJSBridge.getVJSBridge().sendRequestOperation(
        validParams,
        iLinkType,
        (data: ResponseData) => {
          const { errCode = 0, errMsg = '', result = 0, msg, ...responseEntityData } = data;
          if (errCode !== SUCCESS_ERR_CODE || result !== SUCCESS_ERR_CODE) {
            const reportErrCode = errCode || result;
            const reportErrMsg = errMsg || msg;
            // 上报异常
            aegis?.report({
              ext2: cmd,
              ext1: reportErrCode,
              msg: reportErrMsg,
              level: Aegis.logType.AJAX_ERROR,
            });
            rej({
              errCode: reportErrCode,
              errMsg: reportErrMsg,
            });
            return;
          }
          res(responseEntityData as T);
        },
      );
    }, params),
    {
      callbackId: VJSBridgeFront.getCallbackId() - 1,
      params,
    },
  );
  return requestPromise;
};

const mockExecuteCallback = (cmd: string, callbackId: number, params?: OperationPayloadRequestInterior) => {
  const dataForCMD = MockData(params)[cmd];
  if (dataForCMD) {
    console.log('CMD: ', cmd, callbackId);
    const executeOperationData = {
      type: FrontOperationType.ExecuteCallback,
      payload: {
        data: dataForCMD,
        callbackId,
      },
    };
    VJSBridgeFront.postMessage(JSON.stringify(executeOperationData));
  } else {
    console.log(`Development. No mock data for cmd "${cmd}"`);
  }
};

export const timeoutPromiseWrapper = <T>(
  handler: (res: any, rej: any) => void,
  params: OperationPayloadRequestInterior | undefined,
) => new Promise<T>((res, rej) => {
    const timeoutTimer = setTimeout(() => {
      clearTimeout(timeoutTimer);
      try {
        const reportData = params?.cmd ? { cmd: params.cmd } : params;
        // 上报超时
        aegis?.report({
          msg: '请求超时',
          ext1: 'request timeout',
          ext2: JSON.stringify(reportData || {}),
        });
      } catch {}
      rej({
        errCode: TIME_OUT_ERR_CODE,
        errMsg: 'Request timeout.',
      });
    }, TIME_OUT_MS);
    const adaptedRes = (value: any) => {
      clearInterval(timeoutTimer);
      res(value);
    };
    handler(adaptedRes, rej);
  });

export const devAutoExecuteWrapper = <T>(
  cmd = '',
  nextPromise: Promise<T>,
  { delay = 500, callbackId = 0, params }: Partial<{
    delay: number;
    callbackId: number,
    params: OperationPayloadRequestInterior,
  }> = {},
) => {
  if (process.env.NODE_ENV === 'development' && window.location.search.includes('mockData')) {
    // 测试环境自动调用Native2Front回调函数
    setTimeout(() => mockExecuteCallback(cmd, callbackId, params), delay);
  }
  return nextPromise;
};
